import { getBaseURL, getClient } from '../BLL/clients/HttpClient';
import { ApiEndPoint } from '../BLL/Enums/ApiEndPoint';
import {
  FairFareQueryParams,
  getFairFareQueryParams,
} from '../types/interfaces/shared';
import { Bill, BillVerdictRequest } from '../pages/Claims/ClaimDetailsTypes';
import type {
  BulkDiscountJobResponse,
  BulkDiscountJobStatusResponse,
  BulkDiscountRefreshUrlResponse,
} from '../BLL/types/bulkDiscountJobs';

type Claim = {
  gemClaimNo: string
  hipaaWaiverUrl: string
  id: string | number
  medicalRecords: {fileName: string, docUrl: string}[]
  member: { carrier: number },
  patientType: 'Outpatient' | 'Inpatient' | 'Pharmacy'
  policyReferenceNo: string
}
export type BillWithClaim = Bill & {
  // Related claim info
  caseId: string;
  claimType: string; // patientType from Claims
  claimId: string;
  policyNumber: string;
  Claims?: Claim
}

export interface BillsPagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface BillsResponse {
  bills: BillWithClaim[];
  pagination: BillsPagination;
}

export type BillsQuery = FairFareQueryParams & {
  /** Matched against case ID, policy number, bill ID and provider at once. */
  search?: string;
  claimType?: string;
  billType?: string;
};

export const getAllBills = async (
  query: BillsQuery = {},
  carrierId?: number // Was currentTenant
): Promise<BillsResponse> => {
  const authBaseUrl = getBaseURL(ApiEndPoint.Auth);
  const client = getClient({ baseURL: authBaseUrl });

  const params = getFairFareQueryParams(query);
  if (carrierId) {
    params.carrierId = carrierId;
  }
  if (query.status) {
    params.status = query.status;
  }
  if (query.search) {
    params.search = query.search;
  }
  if (query.claimType) {
    params.claimType = query.claimType;
  }
  if (query.billType) {
    params.billType = query.billType;
  }
  const { data } = await client.get<BillsResponse>('v1/bills', {
    params,
  });

  return data;
};

export const getBillById = async (billId: string): Promise<BillWithClaim> => {
  const authBaseUrl = getBaseURL(ApiEndPoint.Auth);
  const client = getClient({ baseURL: authBaseUrl });

  const { data } = await client.get<BillWithClaim>(`v1/bills/${billId}`);

  return data;
}

// New unified API method for bill verdicts
export const submitBillVerdict = async (
  claimId: string,
  verdictData: BillVerdictRequest
) => {
  const authBaseUrl = getBaseURL(ApiEndPoint.Auth);
  const client = getClient({ baseURL: authBaseUrl });
  if (verdictData.verdict === 'pending') {
    // Delete verdict
    return await client.delete(`v1/claims/verdict/${verdictData.billId}`, {
      validateStatus: (status) => status < 400,
    });
  }

  // Create or update verdict
  await client.put(`v1/claims/${claimId}/verdict`, verdictData, {
    validateStatus: (status) => status < 400,
  });
};

export const downloadDiscountDetailsPdf = async (billId: string): Promise<Blob> => {
  const client = getClient({ baseURL: getBaseURL(ApiEndPoint.Auth) });

  // Use arraybuffer instead of blob: axios with responseType:'blob' returns a
  // Blob object as `data`, and wrapping it in `new Blob([blobData])` creates a
  // Blob-of-a-Blob that some browsers silently mishandle (empty or corrupted
  // download). With arraybuffer, `data` is a raw ArrayBuffer — constructing a
  // Blob from it is unambiguous and works reliably across all browsers.
  const { data } = await client.get<ArrayBuffer>(`v1/bills/${billId}/discount-details-pdf`, {
    responseType: 'arraybuffer',
    headers: { Accept: 'application/pdf' },
  });

  const blob = new Blob([data], { type: 'application/pdf' });
  return blob;
};

export const bulkApprovedBills = async (billIds: string[]) => {
    const authBaseUrl = getBaseURL(ApiEndPoint.Auth);
    const client = getClient({ baseURL: authBaseUrl });
    const { data } = await client.post(`v1/bills/bulk-verdict`, { billIds });
    return data;
}

export const createBulkDiscountJob = async (
  billIds: string[],
  carrierId: number,
  idempotencyKey: string
): Promise<BulkDiscountJobResponse> => {
  const client = getClient({ baseURL: getBaseURL(ApiEndPoint.Auth) });
  const { data } = await client.post<BulkDiscountJobResponse>(
    'v1/bills/bulk-discount-details-jobs',
    { billIds, carrierId, idempotencyKey }
  );
  return data;
};

export const pollBulkDiscountJob = async (
  jobId: string
): Promise<BulkDiscountJobStatusResponse> => {
  const client = getClient({ baseURL: getBaseURL(ApiEndPoint.Auth) });
  const { data } = await client.get<BulkDiscountJobStatusResponse>(
    `v1/bills/bulk-discount-details-jobs/${jobId}`
  );
  return data;
};

export const refreshBulkDiscountDownloadUrl = async (
  jobId: string
): Promise<BulkDiscountRefreshUrlResponse> => {
  const client = getClient({ baseURL: getBaseURL(ApiEndPoint.Auth) });
  const { data } = await client.post<BulkDiscountRefreshUrlResponse>(
    `v1/bills/bulk-discount-details-jobs/${jobId}/refresh-download-url`
  );
  return data;
};
